import { useEffect, useState, useCallback } from 'react';
import { useSocket } from './useSocket.js';
import * as utils from '../utils/utils.js';

export const useChat = (sessionId, eventId, onReactionUpdate) => {
    const [messages, setMessages] = useState([]);
    const [typingUsers, setTypingUsers] = useState([]);
    const [isTyping, setIsTyping] = useState(false);
    const [typingTimeouts, setTypingTimeouts] = useState({});
    const socket = useSocket(eventId, sessionId);

    // Handle new messages
    const handleNewMessage = useCallback((message) => {
        setMessages(prev => {
            // Check if message already exists to avoid duplicates
            const exists = prev.some(msg => msg.chat_id === message.chat_id);
            if (exists) return prev;
            
            // Add new message and sort by timestamp
            return [...prev, message].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        });
    }, []);

    // Handle typing indicators with delayed removal
    const handleTypingIndicator = useCallback((data) => {
        const { author, isTyping: userIsTyping } = data;

        if (userIsTyping) {
            // Clear any existing timeout for this user
            setTypingTimeouts(prev => {
                if (prev[author]) {
                    clearTimeout(prev[author]);
                }
                return prev;
            });

            // Add user to typing list if not already there
            setTypingUsers(prev =>
                prev.includes(author) ? prev : [...prev, author]
            );
        } else {
            // Add a 1-second delay before removing the typing indicator
            const timeout = setTimeout(() => {
                setTypingUsers(prev => prev.filter(user => user !== author));
                setTypingTimeouts(prev => {
                    const newTimeouts = { ...prev };
                    delete newTimeouts[author];
                    return newTimeouts;
                });
            }, 2500); // 1 second delay

            setTypingTimeouts(prev => ({
                ...prev,
                [author]: timeout
            }));
        }
    }, []);

    // Handle message reactions
    const handleMessageReaction = useCallback((data) => {
        const { chatId, emotion, participantId } = data;

        // Convert emotion to number to match database format and EmoteIcon expectations
        const emotionNumber = parseInt(emotion, 10);

        setMessages(prev => prev.map(msg => {
            if (msg.chat_id === chatId) {
                // Update emotions array for this message
                const updatedEmotions = msg.emotions || [];
                const existingEmotion = updatedEmotions.find(
                    e => e.participant_id === participantId && e.emotion === emotionNumber
                );

                if (existingEmotion) {
                    // Remove emotion if it already exists (toggle off)
                    return {
                        ...msg,
                        emotions: updatedEmotions.filter(
                            e => !(e.participant_id === participantId && e.emotion === emotionNumber)
                        )
                    };
                } else {
                    // Add new emotion
                    return {
                        ...msg,
                        emotions: [...updatedEmotions, { participant_id: participantId, emotion: emotionNumber }]
                    };
                }
            }
            return msg;
        }));

        // Also notify the parent component (Session.js) about the reaction update
        if (onReactionUpdate && typeof onReactionUpdate === 'function') {
            onReactionUpdate(data);
        }
    }, [onReactionUpdate]);

    // Set up socket listeners
    useEffect(() => {
        if (!socket || typeof socket.on !== 'function') {
            return;
        }

        // Listen for real-time chat events
        socket.on('new-message', handleNewMessage);
        socket.on('user-typing', handleTypingIndicator);
        socket.on('message-reaction', handleMessageReaction);

        return () => {
            if (socket && typeof socket.off === 'function') {
                socket.off('new-message', handleNewMessage);
                socket.off('user-typing', handleTypingIndicator);
                socket.off('message-reaction', handleMessageReaction);
            }

            // Clear all typing timeouts on cleanup
            Object.values(typingTimeouts).forEach(timeout => clearTimeout(timeout));
        };
    }, [socket, handleNewMessage, handleTypingIndicator, handleMessageReaction, typingTimeouts]);

    // Fallback API call for sending messages when Socket.IO fails
    const sendMessageViaAPI = useCallback(async (author, message) => {
        try {
            const response = await fetch(`${utils.backendUrl()}/chat/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    session_id: sessionId,
                    event_id: eventId,
                    author: author,
                    message: message
                })
            });

            if (response.ok) {
                return true;
            } else {
                return false;
            }
        } catch (error) {
            return false;
        }
    }, [sessionId, eventId]);

    // Send a chat message with fallback
    const sendMessage = useCallback(async (author, message) => {
        if (!socket || typeof socket.emit !== 'function' || !sessionId || !eventId) {
            return await sendMessageViaAPI(author, message);
        }

        socket.emit('chat-message', {
            sessionId,
            eventId,
            author,
            message
        });
        return true;
    }, [socket, sessionId, eventId, sendMessageViaAPI]);

    // Send typing indicator
    const sendTypingIndicator = useCallback((author, isTypingNow) => {
        if (!socket || typeof socket.emit !== 'function' || !sessionId) {
            return;
        }

        socket.emit('chat-typing', {
            sessionId,
            author,
            isTyping: isTypingNow
        });
    }, [socket, sessionId]);

    // Send message reaction
    const sendReaction = useCallback((chatId, participantId, emotion) => {
        if (!socket || typeof socket.emit !== 'function' || !sessionId) {
            return;
        }

        socket.emit('chat-reaction', {
            sessionId,
            chatId,
            participantId,
            emotion
        });
    }, [socket, sessionId]);

    return {
        messages,
        setMessages,
        typingUsers,
        isTyping,
        setIsTyping,
        sendMessage,
        sendTypingIndicator,
        sendReaction
    };
};
